/**
 * Forced cache refresh on platform updates (M directive 2026-06-05).
 *
 * Every deploy must automatically dump the browser's cached data — Workbox
 * CacheStorage (precache + runtime tile/API caches), service-worker
 * registrations, and the nest-offline IndexedDB — then reload, so no user
 * ever runs a stale bundle against a new backend. The old UX (a toast
 * politely asking the user to refresh) is exactly what this replaces.
 *
 * What deliberately SURVIVES a dump:
 *   - nest.oauth.* localStorage (Cognito tokens — deploys must not log
 *     anyone out)
 *   - user preferences (theme/font, map + sidebar prefs, chat notify pref)
 *   - Matrix SDK storage (localStorage + its own IndexedDBs) — wiping it
 *     forces device churn and re-login races (see pb #339)
 *
 * Update detection is two-pronged:
 *   1. The service-worker `activated` event (new SW took control — fires on
 *      the deploy that's already been fetched). Wired in main.tsx.
 *   2. A version poller against /version.json (emitted at build time with
 *      the git SHA) — catches long-idle tabs and SW-less contexts. Runs on
 *      an interval and on tab focus.
 *
 * A 60-second guard (localStorage stamp) makes reload loops impossible even
 * if detection misfires — worst case the app reloads once a minute, not in
 * a tight loop.
 */

/** Build identity injected by vite.config.ts (git SHA, or 'dev' locally). */
export const BUILD_ID: string =
  typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev'

export const RELOAD_GUARD_KEY = 'nest:last-forced-reload'

/**
 * The single host where the PWA service worker is allowed to run.
 *
 * The Access-gated testing host (dev-nest.thephenom.app) sits behind
 * Cloudflare Zero-Trust Access: when the Access session lapses, same-origin
 * asset fetches 302 to the interactive Access login, which makes Workbox
 * precaching fail cross-origin and breaks lazy chunk imports. Prod has no
 * Access gate, so the PWA only runs there. (#447)
 */
export const PWA_HOST = 'nest.thephenom.app'

/**
 * Should this client register the PWA service worker?
 * Only on a genuine production build served from the un-gated prod host —
 * never on dev-nest (CF Access), preview deploys, or the dev server.
 */
export function shouldRunPwa(hostname: string, isProd: boolean): boolean {
  return isProd && hostname === PWA_HOST
}

/** Transient localStorage flags that should not outlive a forced refresh. */
const PURGE_LOCALSTORAGE_KEYS = ['vite-preload-reloaded']

/** The app's own offline DB. Matrix SDK databases are NOT listed — ever. */
const PURGE_INDEXED_DBS = ['nest-offline']

const RELOAD_GUARD_WINDOW_MS = 60_000

/**
 * Is the remote build a genuinely different deploy?
 * Empty/missing remote means "couldn't tell" — never reload on that, or a
 * broken /version.json would reload-loop every tab. The 'dev' build id
 * (vite dev server) never updates.
 */
export function isNewBuild(
  current: string,
  remote: string | null | undefined
): boolean {
  if (current === 'dev') return false
  return !!remote && remote !== current
}

/** Reload-loop guard: at most one forced reload per window. */
export function shouldReloadNow(
  lastReloadMs: number | null,
  nowMs: number,
  minIntervalMs: number = RELOAD_GUARD_WINDOW_MS
): boolean {
  if (lastReloadMs === null || Number.isNaN(lastReloadMs)) return true
  return nowMs - lastReloadMs >= minIntervalMs
}

interface DumpDeps {
  caches: {
    keys: () => Promise<string[]>
    delete: (key: string) => Promise<boolean>
  }
  getRegistrations: () => Promise<Array<{ unregister: () => Promise<boolean> }>>
  deleteDatabase: (name: string) => void
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  reload: () => void
  now: () => number
}

function browserDeps(): DumpDeps | null {
  if (typeof window === 'undefined') return null
  return {
    caches:
      'caches' in window
        ? window.caches
        : { keys: () => Promise.resolve([]), delete: () => Promise.resolve(false) },
    getRegistrations: () =>
      'serviceWorker' in navigator
        ? navigator.serviceWorker.getRegistrations()
        : Promise.resolve([]),
    deleteDatabase: (name) => {
      try {
        indexedDB.deleteDatabase(name)
      } catch {
        // best-effort
      }
    },
    storage: window.localStorage,
    reload: () => window.location.reload(),
    now: () => Date.now(),
  }
}

/**
 * Dump every cache layer and reload. Best-effort throughout: a failure in
 * any layer must never stop the reload (a reload with stale caches beats a
 * stale tab that never updates).
 */
export async function dumpCachesAndReload(deps?: DumpDeps): Promise<void> {
  const d = deps ?? browserDeps()
  if (!d) return

  const last = Number(d.storage.getItem(RELOAD_GUARD_KEY) ?? NaN)
  if (!shouldReloadNow(Number.isNaN(last) ? null : last, d.now())) return
  d.storage.setItem(RELOAD_GUARD_KEY, String(d.now()))

  // 1. Service workers — re-register fresh on next load
  try {
    const regs = await d.getRegistrations()
    await Promise.allSettled(regs.map((r) => r.unregister()))
  } catch {
    /* best-effort */
  }

  // 2. CacheStorage — runtime caches ONLY. NEVER the workbox precache: an
  // unregistered worker keeps CONTROLLING the page until the next navigation,
  // and gutting its precache mid-flight blanks the app shell (dev-nest
  // blank-page regression, 2026-06-05). The incoming build's SW purges old
  // precaches itself via cleanupOutdatedCaches() after takeover.
  try {
    const keys = await d.caches.keys()
    await Promise.allSettled(
      keys
        .filter((k) => !k.startsWith('workbox-precache'))
        .map((k) => d.caches.delete(k))
    )
  } catch {
    /* best-effort */
  }

  // 3. App offline DB (Matrix DBs untouched)
  for (const name of PURGE_INDEXED_DBS) d.deleteDatabase(name)

  // 4. Transient flags only — auth + prefs survive
  for (const key of PURGE_LOCALSTORAGE_KEYS) {
    try {
      d.storage.removeItem(key)
    } catch {
      /* best-effort */
    }
  }

  d.reload()
}

/**
 * Start the /version.json poller (detection prong 2). Returns a stop
 * function. Polls on an interval and on tab focus; on a new build, dumps
 * caches and reloads automatically.
 */
export function startUpdateWatcher(
  opts: { intervalMs?: number; onUpdate?: () => void } = {}
): () => void {
  if (typeof window === 'undefined' || BUILD_ID === 'dev') return () => {}
  const intervalMs = opts.intervalMs ?? 5 * 60_000

  let stopped = false
  async function check() {
    if (stopped || document.visibilityState === 'hidden') return
    try {
      const res = await fetch(`/version.json?_=${Date.now()}`, {
        cache: 'no-store',
      })
      if (!res.ok) return
      const data = (await res.json()) as { build?: string }
      if (isNewBuild(BUILD_ID, data.build)) {
        opts.onUpdate?.()
        // Give the toast a beat to paint before the reload.
        setTimeout(() => void dumpCachesAndReload(), 1200)
      }
    } catch {
      // offline / fetch failed — try again next tick
    }
  }

  const timer = window.setInterval(() => void check(), intervalMs)
  const onVisible = () => {
    if (document.visibilityState === 'visible') void check()
  }
  document.addEventListener('visibilitychange', onVisible)

  return () => {
    stopped = true
    window.clearInterval(timer)
    document.removeEventListener('visibilitychange', onVisible)
  }
}
